import React, { useEffect, useState } from 'react';
import { useSession } from '@/hooks/useSession';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FeedCommentAnswers } from '@/pages/feed/feed-comment-answers';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { PostActionsButton, PostActionsItem, PostActionsWrapper, PostComment, PostCommentBody, PostCommentContent, PostCommentHeader, PostCommentName, PostCommentText, PostCommentTime } from '@/components/custom/post';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { FiTrash2 } from 'react-icons/fi';
import { Loader } from '@/components/custom/loader';

export function CourseClassroomCommentsCard(props) {
  const { comment, deleteCommentFromParent, comments, learningSystemItemId } = props;
  const { session } = useSession();
  const client = useClient();
  const authorityLevel = useAuthorityLevel(session);
  const [isOpenAnswer, setIsOpenAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const findAnswers = () => {
    const commentsWithAnswers = comments?.filter(comment => comment?.['comment-parent-id'] !== null);
    const answersByCommentId = commentsWithAnswers?.filter(item => item?.['comment-parent-id'] === comment?.id).reverse();

    setAnswers(answersByCommentId);
  };

  const handleCreateAnswer = async value => {
    try {
      const response = await client.mutate('/comments', {
        'comment-parent-id': comment.id,
        body: value,
        'learning-system-item-id': learningSystemItemId
      });

      setAnswers(prev => [response.data, ...prev]);
    } catch (error) {
      console.log(error);
      toast.error('Erro ao responder');
    }
  };

  const handleDeleteAnswer = async answerId => {
    setIsDeleting(true);

    try {
      const { error } = await client.delete(`/comments/${answerId}`);

      if (error) {
        console.log(error);
        toast.error('Erro ao deletar resposta');
        return;
      }

      setAnswers(prev => prev.filter(item => item.id !== answerId));
    } catch (error) {
      console.error('Erro de rede/execução:', error);
      toast.error('Erro ao deletar resposta');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    findAnswers();
  }, [comments, comment]);

  const handleMainCommentDelete = () => {
    if (deleteCommentFromParent) {
      deleteCommentFromParent(comment, setIsDeleting);
    }
  };

  return (
    <PostComment>
      {isDeleting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
          <Loader />
        </div>
      )}

      <Link
        to={`/usuario/metricas/${comment?.user?.id}`}
        state={{ isRedirected: true }}
      >
        <AvatarWrapper
          size="sm"
          src={comment?.user?.image?.url}
          text={comment?.user?.name}
        />
      </Link>

      <PostCommentContent>
        <PostCommentHeader>
          <PostCommentName>
            <Link
              to={`/usuario/metricas/${comment?.user?.id}`}
              state={{ isRedirected: true }}
            >
              {comment?.user?.name || 'Usuário do sistema'}
            </Link>
          </PostCommentName>
          <PostCommentTime>{moment(comment['created-at']).fromNow()}</PostCommentTime>
        </PostCommentHeader>

        <PostCommentBody>
          <PostCommentText>{comment.body}</PostCommentText>
        </PostCommentBody>

        <FeedCommentAnswers
          answers={answers}
          isOpenAnswer={isOpenAnswer}
          onSubmit={handleCreateAnswer}
          onDelete={handleDeleteAnswer}
        />
      </PostCommentContent>

      <div className="flex flex-col gap-2 pt-8">
        <Button
          variant="link"
          size="xs"
          style={{ opacity: isOpenAnswer ? 0.5 : 1 }}
          onClick={() => setIsOpenAnswer(prev => !prev)}
        >
          Responder
        </Button>

        {(authorityLevel === 'admin' || comment?.user?.id === session?.user?.id) && (
          <PostActionsWrapper className="w-full flex-row">
            <PostActionsItem className="flex grow flex-row justify-end">
              <PostActionsButton
                isDanger
                onClick={handleMainCommentDelete}
                className="mr-0"
              >
                <FiTrash2 size={16} />
              </PostActionsButton>
            </PostActionsItem>
          </PostActionsWrapper>
        )}
      </div>
    </PostComment>
  );
}
